//SiteHealthScoreCard.js - Updated with date range support
import React, { useState, useEffect } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import './SiteHealthScoreCard.css';

/**
 * Interactive Health Score Card Component with glassmorphism design
 * Updated to show date range in title
 */
const SiteHealthScoreCard = ({
  siteId,
  score = 87, 
  week = 3.2, 
  month = -5,
  isLoading = false,
  dateRange = 'all' // Add dateRange parameter
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Helper function to get display text for date range
  const getDateRangeDisplayText = (range) => {
    const rangeMap = {
      'today': 'Today',
      'yesterday': 'Yesterday', 
      'week': 'Past Week',
      'month': 'Past Month',
      'year': 'Past Year',
      'all': 'All Time'
    };
    return rangeMap[range] || 'All Time';
  };
  
  // Animate the score counter on mount or when score changes
  useEffect(() => {
    setIsVisible(true);
    if (!isLoading && score > 0) {
      const timer = setTimeout(() => {
        animateCounter(score);
      }, 300);
      return () => clearTimeout(timer);
    } else if (!isLoading && score === 0) {
      // If score is 0, set animated score to 0 immediately
      setAnimatedScore(0);
    }
  }, [score, isLoading]);
  
  const animateCounter = (targetScore) => {
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const increment = targetScore / steps;
    let currentScore = 0;
    
    const timer = setInterval(() => {
      currentScore += increment;
      if (currentScore >= targetScore) {
        setAnimatedScore(targetScore);
        clearInterval(timer);
      } else {
        setAnimatedScore(Math.floor(currentScore));
      }
    }, duration / steps);
  };
  
  // Determine color and status based on score
  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e'; // Emerald green
    if (score >= 60) return '#f59e0b'; // Amber
    if (score >= 40) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };
  
  const getScoreStatus = (score) => {
    if (score >= 80) return { text: 'Excellent', class: 'excellent' };
    if (score >= 60) return { text: 'Good', class: 'good' };
    if (score >= 40) return { text: 'Average', class: 'average' };
    return { text: 'Needs Attention', class: 'poor' };
  };
  
  const scoreColor = getScoreColor(score);
  const status = getScoreStatus(score);
  const getScoreClass = (score) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-average';
    return 'score-poor';
  };
  const scoreClass = getScoreClass(score);
  
  // Format change with + or - prefix
  const formatChange = (change) => {
    if (change === 0) return '0.0%';
    return change > 0 ? `↗ +${Math.abs(change).toFixed(1)}%` : `↘ ${change.toFixed(1)}%`;
  };
  
  // Determine class for change indicators
  const getChangeClass = (change) => {
    return change > 0 ? 'trend-positive' : change < 0 ? 'trend-negative' : 'trend-neutral';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="health-score-card loading">
        <div className="health-loading-spinner" />
        <div className="health-loading-text">
          Calculating health score...
        </div>
      </div>
    );
  }

  // No data state
  if (score === 0 && week === 0 && month === 0) {
    return (
      <div className="health-score-card no-data">
        <div className="health-no-data-content">
          <div className="health-no-data-icon">📊</div>
          <div className="health-no-data-title">No Health Data</div>
          <div className="health-no-data-subtitle">
            No feedback data available for {getDateRangeDisplayText(dateRange).toLowerCase()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`health-score-card ${isVisible ? 'visible' : ''} ${isHovered ? `hovered ${status.class}` : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        // Add click functionality if needed
        console.log('Health score card clicked:', { siteId, score, dateRange });
      }}
    >
      
      {/* Enhanced gradient overlay that responds to hover */}
      <div className={`health-gradient-overlay ${isHovered ? status.class : ''}`} />
      
      {/* Header - Updated to show date range */}
      <div className="health-score-header">
        <h3 className="health-score-title">
          FDBK Score ({getDateRangeDisplayText(dateRange)})
        </h3>
      </div>
      
      {/* Circular Progress */}
      <div className="health-progress-container">
        <CircularProgressbar
          value={animatedScore}
          maxValue={100}
          text=""
          styles={buildStyles({
            rotation: 0,
            strokeLinecap: 'round',
            pathColor: scoreColor,
            textColor: '#1f2937',
            trailColor: 'rgba(0, 0, 0, 0.05)',
            backgroundColor: 'transparent',
            pathTransitionDuration: 0.5,
          })}
        />
        
        {/* Custom centered text */}
        <div className="health-score-text">
          <div className={`health-score-value ${scoreClass}`}>
            {animatedScore}
          </div>
          <div className="health-score-max">
            /100
          </div>
        </div>
      </div>
      
      {/* Status */}
      <div className="health-status-container">
        <div className={`health-status-text ${isHovered ? status.class : ''}`}>
          {status.text}
        </div>
      </div>
      
      {/* Trend indicators - Updated labels based on dateRange */}
      <div className="health-trends-container">
        <div className="health-trend-item">
          <div className={`health-trend-value ${getChangeClass(week)}`}>
            {formatChange(week)}
          </div>
          <div className="health-trend-label">
            {getTrendLabel('week', dateRange)}
          </div>
        </div>
        
        <div className="health-trend-separator" />
        
        <div className="health-trend-item">
          <div className={`health-trend-value ${getChangeClass(month)}`}>
            {formatChange(month)}
          </div>
          <div className="health-trend-label">
            {getTrendLabel('month', dateRange)}
          </div>
        </div>
      </div>
      
      {/* Ripple effect for clicks */}
      {isHovered && (
        <div 
          className="health-ripple-effect"
          style={{
            background: `radial-gradient(circle, ${scoreColor}10 0%, transparent 70%)`
          }}
        />
      )}
    </div>
  );
  
  // Helper function to get appropriate trend labels based on date range
  function getTrendLabel(trendType, currentDateRange) {
    if (currentDateRange === 'today') {
      return trendType === 'week' ? 'vs yesterday' : 'week';
    } else if (currentDateRange === 'week') {
      return trendType === 'week' ? 'vs last week' : 'month';
    } else if (currentDateRange === 'month') {
      return trendType === 'week' ? 'week' : 'vs last month';
    } else {
      // For 'all', 'year', etc., use generic labels
      return trendType === 'week' ? 'week' : 'month';
    }
  }
};

export default SiteHealthScoreCard;